import { forwardRef, type ReactNode } from 'react';
import {
  Pressable,
  type PressableProps,
  type StyleProp,
  StyleSheet,
  View,
  type ViewProps,
  type ViewStyle,
} from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { HStack, VStack } from './Stack';
import { Text } from './Text';

type ListRowAs = 'view' | 'pressable';

interface BaseListRowProps {
  leading?: ReactNode;
  title: string;
  subtitle?: string;
  trailing?: ReactNode;
  divider?: boolean;
  disabled?: boolean;
  destructive?: boolean;
  style?: StyleProp<ViewStyle>;
  as?: ListRowAs;
}

type ListRowViewProps = BaseListRowProps & {
  as?: 'view';
} & Omit<ViewProps, 'style' | 'children'>;

type ListRowPressableProps = BaseListRowProps & {
  as: 'pressable';
} & Omit<PressableProps, 'style' | 'children'>;

export type ListRowProps = ListRowViewProps | ListRowPressableProps;

export const ListRow = forwardRef<View, ListRowProps>(function ListRow(props, ref) {
  const {
    leading,
    title,
    subtitle,
    trailing,
    divider = true,
    disabled = false,
    destructive = false,
    style,
    as = 'view',
    ...rest
  } = props;

  const titleColor = destructive
    ? colors.status.error.main
    : disabled
      ? colors.text.disabled
      : colors.text.primary;

  const body = (
    <HStack spacing="md" align="center" style={styles.row}>
      {leading ? <View style={styles.leading}>{leading}</View> : null}
      <VStack spacing="xxs" style={styles.body}>
        <Text variant="body" color={titleColor} style={styles.title}>
          {title}
        </Text>
        {subtitle ? (
          <Text variant="caption" color={colors.text.tertiary}>
            {subtitle}
          </Text>
        ) : null}
      </VStack>
      {trailing ? <View style={styles.trailing}>{trailing}</View> : null}
    </HStack>
  );

  const containerStyle: StyleProp<ViewStyle> = [
    divider && styles.divider,
    disabled && styles.disabled,
    style,
  ];

  if (as === 'pressable') {
    return (
      <Pressable
        ref={ref}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityState={{ disabled }}
        {...(rest as PressableProps)}
        style={({ pressed }) => [containerStyle, pressed && !disabled && styles.pressed]}
      >
        {body}
      </Pressable>
    );
  }

  return (
    <View ref={ref} {...(rest as ViewProps)} style={containerStyle}>
      {body}
    </View>
  );
});

const styles = StyleSheet.create({
  row: {
    paddingVertical: tokens.spacing.md,
  },
  divider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border.subtle,
  },
  leading: {
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  trailing: {
    flexShrink: 0,
  },
  title: {
    fontWeight: '500',
  },
  pressed: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
  disabled: {
    opacity: 0.5,
  },
});
